/**
 * One-time migration: localStorage custom reports → Supabase saved_reports.
 *
 * Phase 1 of the Reports reorganization retires the legacy per-browser custom
 * report store (`localStorage["custom-reports-<workspaceId>"]`, build-contract
 * §2.2 violation) in favor of the durable, workspace-shared `saved_reports`
 * table behind `useSavedReportsStore`.
 *
 * This importer runs once per browser: it reads any legacy reports for the
 * given workspace, inserts them via the store (which persists to Supabase),
 * then clears the legacy key so it never re-imports. It is a no-op when there
 * is nothing to migrate or when Supabase is unavailable (so local data is
 * never lost before it can be persisted).
 *
 * See docs/1-canon/DEPRECATION_REGISTER.md.
 */

import { useSavedReportsStore } from '../stores/prioritization/savedReportsStore';
import { isSupabaseAvailable } from './supabaseSync';
import type { FilterGroup, SortSpec } from '../components/prioritization/item-list/hooks/useItemListState';

/** Legacy localStorage shape (formerly exported from CustomReportsLibraryPage). */
interface LegacyCustomReport {
  id: string;
  name: string;
  description?: string;
  filterGroups: unknown[];
  sort: { field: string; direction: 'asc' | 'desc' }[];
  groupBy: string | null;
  visibleColumns: string[];
  createdAt: string;
  updatedAt: string;
}

function legacyStorageKey(workspaceId: string): string {
  return `custom-reports-${workspaceId}`;
}

function readLegacyReports(workspaceId: string): LegacyCustomReport[] {
  try {
    const raw = localStorage.getItem(legacyStorageKey(workspaceId));
    return raw ? (JSON.parse(raw) as LegacyCustomReport[]) : [];
  } catch {
    return [];
  }
}

/**
 * Import any legacy localStorage custom reports for `workspaceId` into the
 * Supabase-backed saved reports store, then clear the legacy key.
 *
 * Returns the number of reports imported. Idempotent: once the legacy key is
 * cleared, subsequent calls do nothing.
 *
 * Call this AFTER `savedReportsStore.fetchFromSupabase()` resolves, so the
 * optimistic inserts are not clobbered by the initial fetch.
 */
export function migrateLocalCustomReports(workspaceId: string): number {
  // Without a backend we cannot durably persist — leave the legacy data in
  // place rather than clearing it and losing the user's reports.
  if (!isSupabaseAvailable()) return 0;

  const legacy = readLegacyReports(workspaceId);
  if (legacy.length === 0) {
    // Nothing to migrate; make sure a stray empty key doesn't linger.
    try { localStorage.removeItem(legacyStorageKey(workspaceId)); } catch { /* ignore */ }
    return 0;
  }

  const { createReport } = useSavedReportsStore.getState();

  for (const r of legacy) {
    createReport({
      name: r.name,
      description: r.description,
      folderId: null,
      filterGroups: (r.filterGroups as FilterGroup[]) ?? [],
      sort: (r.sort as SortSpec[]) ?? [],
      groupBy: r.groupBy ?? null,
      visibleColumns: r.visibleColumns ?? [],
      activeChart: 'category',
    });
  }

  // Clear the legacy key so this browser never re-imports.
  try { localStorage.removeItem(legacyStorageKey(workspaceId)); } catch { /* ignore */ }

  return legacy.length;
}
